import { existsSync } from "fs";
import { resolve } from "path";
import type { GencowAuthConfig, GencowConfig } from "@gencow/core";
import { collectRuntimeOperationInventory } from "./runtime-operation-inventory.js";
import {
  auditAIBypass,
  auditFrontendAntiPatterns,
  auditFunctions,
  auditSchemaPatterns,
  auditSelfFetch,
  auditWorkflowShadowing,
  printAIBypassReport,
  printAuditReport,
  printFrontendAntiPatternReport,
  printSelfFetchReport,
  printWorkflowShadowingReport,
} from "./auditor";
import { auditUnsafeDbUsage, printUnsafeDbReport } from "./auditor-unsafe-db";

export function resolveFunctionsPath(envFunctions = process.env.GENCOW_FUNCTIONS): string {
  return envFunctions
    ? resolve(process.cwd(), envFunctions)
    : resolve(import.meta.dirname, "./example/functions");
}

export function hasFunctionsSource(functionsPath: string): boolean {
  return (
    existsSync(resolve(functionsPath, "index.ts")) ||
    existsSync(resolve(functionsPath, "index.js")) ||
    existsSync(`${functionsPath}.ts`) ||
    existsSync(`${functionsPath}.js`)
  );
}

export function resolveFunctionsEntryPoint(functionsPath: string): string {
  if (existsSync(`${functionsPath}.ts`)) return `${functionsPath}.ts`;
  if (existsSync(`${functionsPath}.js`)) return `${functionsPath}.js`;
  if (existsSync(resolve(functionsPath, "index.js"))) return resolve(functionsPath, "index.js");
  return resolve(functionsPath, "index.ts");
}

export function resolveFunctionsImportPath(functionsPath: string): string {
  if (existsSync(resolve(functionsPath, "index.js"))) return resolve(functionsPath, "index.js");
  if (existsSync(resolve(functionsPath, "index.ts"))) return resolve(functionsPath, "index.ts");
  return functionsPath;
}

export function resolveAuthConfigFile(functionsPath: string): string | null {
  const authConfigTs = resolve(functionsPath, "auth.ts");
  if (existsSync(authConfigTs)) return authConfigTs;

  const authConfigJs = resolve(functionsPath, "auth.js");
  return existsSync(authConfigJs) ? authConfigJs : null;
}

const BETTER_AUTH_SCHEMA_EXPORTS = ["user", "session", "account", "verification"] as const;

export function validateBetterAuthSchemaExports(authSchemaModule: Record<string, unknown>): {
  ok: boolean;
  missingExports: string[];
} {
  const missingExports = BETTER_AUTH_SCHEMA_EXPORTS.filter((exportName) => !(exportName in authSchemaModule));
  return { ok: missingExports.length === 0, missingExports };
}

export function resolveProjectConfigFile(functionsPath: string): string | null {
  const projectDir = resolve(functionsPath, "..");
  const configJs = resolve(projectDir, "gencow.config.js");
  if (existsSync(configJs)) return configJs;

  const configTs = resolve(projectDir, "gencow.config.ts");
  return existsSync(configTs) ? configTs : null;
}

async function runStartupAudits(functionsPath: string): Promise<void> {
  const entryPoint = resolveFunctionsEntryPoint(functionsPath);
  const auditResult = await auditFunctions(entryPoint);
  printAuditReport(auditResult, entryPoint);

  if (!auditResult.passed && process.env.GENCOW_STRICT === "true") {
    console.error("[gencow] Server startup aborted due to module violations.");
    console.error("[gencow] Set GENCOW_STRICT=false to override (not recommended).");
    process.exit(1);
  }

  const schemaFile = resolve(functionsPath, "schema.ts");
  if (existsSync(schemaFile)) {
    auditSchemaPatterns(schemaFile);
  }

  const selfFetchResult = auditSelfFetch(functionsPath);
  printSelfFetchReport(selfFetchResult);

  const unsafeDbResult = auditUnsafeDbUsage(functionsPath);
  printUnsafeDbReport(unsafeDbResult);

  if (unsafeDbResult.blocked && process.env.GENCOW_STRICT === "true") {
    console.error("[gencow] Server startup aborted due to unsafe public database access.");
    console.error("[gencow] Use ctx.db or add a complete gencow-allow-unsafe-db review comment.");
    process.exit(1);
  }

  const workflowShadowingResult = auditWorkflowShadowing(functionsPath);
  printWorkflowShadowingReport(workflowShadowingResult);

  if (!workflowShadowingResult.passed && process.env.GENCOW_STRICT === "true") {
    console.error("[gencow] Server startup aborted due to workflow runtime shadowing.");
    console.error('[gencow] Import workflow from "@gencow/core" and remove local workflow.ts forks.');
    process.exit(1);
  }

  const aiBypassResult = auditAIBypass(functionsPath);
  printAIBypassReport(aiBypassResult);

  const projectDir = resolve(functionsPath, "..");
  const frontendResult = auditFrontendAntiPatterns(projectDir);
  printFrontendAntiPatternReport(frontendResult);
}

type RegisteredOperationCounts = {
  legacyQueries: number;
  legacyMutations: number;
  procedureQueries: number;
  procedureMutations: number;
  totalQueries: number;
  totalMutations: number;
  totalOperations: number;
};

export function collectRegisteredOperationCounts(appModule?: unknown): RegisteredOperationCounts {
  const inventory = collectRuntimeOperationInventory({ appModule });
  return {
    legacyQueries: inventory.legacyQueries.length,
    legacyMutations: inventory.legacyMutations.length,
    procedureQueries: inventory.procedureQueries.length,
    procedureMutations: inventory.procedureMutations.length,
    totalQueries: inventory.totalQueries,
    totalMutations: inventory.totalMutations,
    totalOperations: inventory.totalOperations,
  };
}

function logRegisteredOperationCounts(label: string, appModule?: unknown): RegisteredOperationCounts {
  const counts = collectRegisteredOperationCounts(appModule);
  console.log(
    `[gencow] ${label}: ${counts.totalQueries} queries, ${counts.totalMutations} mutations registered`,
  );
  return counts;
}

async function forceImportRegisteredModules(
  functionsPath: string,
  appModule: Record<string, unknown>,
): Promise<void> {
  console.warn("[gencow] Registry empty after import — forcing individual module imports...");

  for (const key of Object.keys(appModule)) {
    try {
      const tsPath = resolve(functionsPath, `${key}.ts`);
      const jsPath = resolve(functionsPath, `${key}.js`);
      const modulePath = existsSync(tsPath) ? tsPath : existsSync(jsPath) ? jsPath : null;
      if (modulePath) {
        await import(modulePath);
        console.log(`[gencow]   ✓ Force-imported: ${key}`);
      }
    } catch (error: any) {
      console.error(`[gencow]   ✗ Failed to import ${key}:`, error?.message);
    }
  }

  const counts = logRegisteredOperationCounts("After force-import", appModule);

  if (counts.totalOperations === 0) {
    console.error("[gencow] ⚠ Still 0 functions after force-import! Possible causes:");
    console.error(`[gencow]   - @gencow/core not resolvable from ${functionsPath}`);
    console.error("[gencow]   - Module files are empty or have syntax errors");
    console.error(`[gencow]   - NODE_PATH=${process.env.NODE_PATH}`);
  }
}

export async function loadAppModule(params: {
  functionsPath: string;
  isWarmWorker: boolean;
  onTiming?: (label: string) => void;
}): Promise<any> {
  const { functionsPath, isWarmWorker, onTiming } = params;

  if (!hasFunctionsSource(functionsPath)) {
    console.log("[gencow] No functions found — starting in minimal mode (deploy to add functions)");
    return {};
  }

  if (!isWarmWorker) {
    await runStartupAudits(functionsPath);
  }

  onTiming?.("pre-import");
  const appModule = await import(resolveFunctionsImportPath(functionsPath));
  onTiming?.("post-import");

  const counts = logRegisteredOperationCounts("After import", appModule);

  if (counts.totalOperations === 0 && appModule) {
    await forceImportRegisteredModules(functionsPath, appModule as Record<string, unknown>);
  }

  return appModule;
}

export async function loadAuthConfig(functionsPath: string): Promise<GencowAuthConfig | undefined> {
  const authConfigFile = resolveAuthConfigFile(functionsPath);
  if (!authConfigFile) return undefined;

  try {
    const authModule = await import(authConfigFile);
    console.log("[auth] Loaded auth config from auth.ts ✓");
    return authModule.default;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    console.error("[auth] Failed to load auth.ts:", message);
    return undefined;
  }
}

export function resolveProjectSchemaFiles(functionsPath: string, projectConfig?: GencowConfig): string[] {
  const projectDir = resolve(functionsPath, "..");
  const configuredSchema = projectConfig?.schema ?? resolve(functionsPath, "schema.ts");
  const schemaEntries = Array.isArray(configuredSchema) ? configuredSchema : [configuredSchema];
  return schemaEntries.map((entry) => resolve(projectDir, entry));
}

function isMigratedAuthSchemaLayout(functionsPath: string, configuredSchemaFiles: readonly string[]): boolean {
  const userAuthSchemaTs = resolve(functionsPath, "schema-auth.ts");
  if (existsSync(userAuthSchemaTs)) return true;
  const userAuthSchemaJs = resolve(functionsPath, "schema-auth.js");
  if (existsSync(userAuthSchemaJs)) return true;
  return configuredSchemaFiles.some((schemaFile) => {
    const normalized = schemaFile.replace(/\\/g, "/");
    return normalized.endsWith("/schema-auth.ts") || normalized.endsWith("/schema-auth.js");
  });
}

/** Legacy unmigrated apps: generated/auth-schema.ts, then root auth-schema.ts. */
export function resolveLegacyAuthSchemaFile(functionsPath: string): string | null {
  const generatedTs = resolve(functionsPath, "generated", "auth-schema.ts");
  if (existsSync(generatedTs)) return generatedTs;

  const generatedJs = resolve(functionsPath, "generated", "auth-schema.js");
  if (existsSync(generatedJs)) return generatedJs;

  const authSchemaTs = resolve(functionsPath, "auth-schema.ts");
  if (existsSync(authSchemaTs)) return authSchemaTs;

  const authSchemaJs = resolve(functionsPath, "auth-schema.js");
  return existsSync(authSchemaJs) ? authSchemaJs : null;
}

function resolveAuthAdapterSchemaCandidates(
  functionsPath: string,
  projectConfig?: GencowConfig,
): string[] {
  const configuredSchemaFiles = resolveProjectSchemaFiles(functionsPath, projectConfig).filter((schemaFile) =>
    existsSync(schemaFile),
  );
  if (isMigratedAuthSchemaLayout(functionsPath, configuredSchemaFiles)) {
    return configuredSchemaFiles;
  }

  const legacyAuthSchemaFile = resolveLegacyAuthSchemaFile(functionsPath);
  if (!legacyAuthSchemaFile) {
    return configuredSchemaFiles;
  }

  const legacyResolved = resolve(legacyAuthSchemaFile);
  if (configuredSchemaFiles.some((schemaFile) => resolve(schemaFile) === legacyResolved)) {
    return configuredSchemaFiles;
  }

  return [...configuredSchemaFiles, legacyResolved];
}

export async function loadAuthAdapterSchema(
  functionsPath: string,
  projectConfig?: GencowConfig,
  options: { isPlatform?: boolean } = {},
): Promise<Record<string, unknown> | undefined> {
  if (options.isPlatform) {
    console.log("[auth] Platform runtime uses built-in Better Auth schema; skipping app schema import");
    return undefined;
  }

  const schemaFiles = resolveAuthAdapterSchemaCandidates(functionsPath, projectConfig);
  if (schemaFiles.length === 0) return undefined;

  const invalid: Array<{ file: string; missingExports: string[] }> = [];

  try {
    for (const schemaFile of schemaFiles) {
      const schemaModule = (await import(schemaFile)) as Record<string, unknown>;
      const validation = validateBetterAuthSchemaExports(schemaModule);
      if (validation.ok) {
        const configured = resolveProjectSchemaFiles(functionsPath, projectConfig)
          .map((entry) => resolve(entry))
          .includes(resolve(schemaFile));
        const label = configured
          ? `configured schema entry ${schemaFile}`
          : `legacy auth schema ${schemaFile}`;
        console.log(`[auth] Loaded Better Auth adapter schema from ${label} ✓`);
        return schemaModule;
      }
      invalid.push({ file: schemaFile, missingExports: validation.missingExports });
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`[auth] Failed to load configured schema entry for Better Auth adapter: ${message}`);
  }

  if (invalid.length > 0) {
    const details = invalid
      .map((entry) => `${entry.file} missing ${entry.missingExports.join(", ")}`)
      .join("; ");
    throw new Error(
      `[auth] Invalid Better Auth schema exports in configured schema entries: ${details}. ` +
        "Auth runtime schema must export user, session, account, and verification.",
    );
  }

  return undefined;
}

export async function loadProjectConfig(functionsPath: string): Promise<GencowConfig | undefined> {
  const projectConfigFile = resolveProjectConfigFile(functionsPath);
  if (!projectConfigFile) return undefined;

  try {
    const configModule = await import(projectConfigFile);
    console.log("[config] Loaded gencow.config ✓");
    return configModule.default;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    console.error("[config] Failed to load gencow.config:", message);
    return undefined;
  }
}
